import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  usePsaPicklists,
  useUpdatePsaConnection,
  type AutotaskConfig,
  type Picklist,
  type PsaConnection,
  type PriorityMap,
} from '@/hooks/usePsa.ts';
import { apiErrorMessage } from '@/utils/apiError.ts';
import { initialDraft } from './autotaskDraft.ts';
import { PicklistSelect } from './PicklistSelect.tsx';
import { picklistOptions as options, SEVERITIES } from './psaSettings.ts';
import { Button } from '@/components/ui/button.tsx';
import { Checkbox } from '@/components/ui/checkbox.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';

/**
 * Autotask's settings for a connection, offered from Autotask's own lists
 * (docs/PsaIntegration.md, "The neutral layer and the PSA operations").
 *
 * Every value is stored as Autotask's ID and shown by its label. Labels are per
 * instance and translated, so nothing here is matched by name — except one
 * preselection: who may see a note starts on the option whose label says
 * "internal", because notes are internal unless the admin chooses otherwise
 * (decision 4). It is only a starting point; the admin sees it and saves it.
 */

export function AutotaskSettings({ connection }: { connection: PsaConnection }) {
  const picklists = usePsaPicklists(connection.id, true);
  if (picklists.isLoading) {
    return (
      <p className="text-sm text-muted-foreground flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Reading Autotask’s lists…
      </p>
    );
  }
  if (picklists.isError) {
    return (
      <p className="text-sm text-destructive">
        {apiErrorMessage(picklists.error, 'Couldn’t read Autotask’s lists.')}
      </p>
    );
  }
  return <AutotaskSettingsForm connection={connection} picklists={picklists.data ?? []} />;
}

function AutotaskSettingsForm({ connection, picklists }: { connection: PsaConnection; picklists: Picklist[] }) {
  const [draft, setDraft] = useState<AutotaskConfig>(() => initialDraft(connection.config, picklists));
  const update = useUpdatePsaConnection(connection.id);
  const set = (patch: Partial<AutotaskConfig>) => { setDraft((d) => ({ ...d, ...patch })); };
  const priorities = draft.priority_map ?? {};
  const statuses = options(picklists, 'status');
  const subIssues = options(picklists, 'subIssueType').filter(
    (o) => draft.issue_type != null && o.parent === String(draft.issue_type),
  );
  const ackIds = draft.ack_status_ids ?? [];
  const missing =
    SEVERITIES.some((s) => priorities[s.key] == null) ||
    draft.new_status_id == null ||
    draft.note_type_id == null ||
    draft.note_publish == null;
  const field = (name: string) => `psa-${connection.id}-${name}`;

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        update.mutate({ config: draft });
      }}
    >
      <fieldset className="space-y-3">
        <legend className="text-sm font-medium mb-1">New tickets</legend>
        <PicklistSelect id={field('status')} label="Status" choices={statuses}
          value={draft.new_status_id} onChange={(v) => { set({ new_status_id: v }); }} />
        <PicklistSelect id={field('queue')} label="Queue" choices={options(picklists, 'queueID')}
          value={draft.queue_id} onChange={(v) => { set({ queue_id: v }); }} noneLabel="No queue" />
        <PicklistSelect id={field('category')} label="Ticket category" choices={options(picklists, 'ticketCategory')}
          value={draft.ticket_category_id} onChange={(v) => { set({ ticket_category_id: v }); }}
          noneLabel="Autotask’s default" />
        <PicklistSelect id={field('type')} label="Ticket type" choices={options(picklists, 'ticketType')}
          value={draft.ticket_type} onChange={(v) => { set({ ticket_type: v }); }} noneLabel="Autotask’s default" />
        <PicklistSelect id={field('source')} label="Source" choices={options(picklists, 'source')}
          value={draft.source_id} onChange={(v) => { set({ source_id: v }); }} noneLabel="None"
          help="Kaseya asks integrations to use a source named after them, so these tickets can be told apart." />
        <PicklistSelect id={field('issue')} label="Issue type" choices={options(picklists, 'issueType')}
          value={draft.issue_type}
          onChange={(v) => { set({ issue_type: v, sub_issue_type: null }); }} noneLabel="None" />
        {draft.issue_type != null ? (
          <PicklistSelect id={field('subissue')} label="Sub-issue type" choices={subIssues}
            value={draft.sub_issue_type} onChange={(v) => { set({ sub_issue_type: v }); }} noneLabel="None" />
        ) : null}
        <div className="space-y-1">
          <Label htmlFor={field('due')}>Due in (hours)</Label>
          <Input id={field('due')} type="number" min={1} max={8760} className="w-32"
            value={draft.due_in_hours ?? ''}
            onChange={(e) => { set({ due_in_hours: e.target.value === '' ? null : Number(e.target.value) }); }} />
          <p className="text-xs text-muted-foreground">
            Autotask requires a due date unless the ticket category sets one.
          </p>
        </div>
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-sm font-medium mb-1">Priority for each severity</legend>
        <p className="text-xs text-muted-foreground">
          Severity sets the ticket’s priority, one way: a priority changed in Autotask isn’t copied back.
        </p>
        {SEVERITIES.map((s) => (
          <PicklistSelect key={s.key} id={field(`priority-${s.key}`)} label={s.label}
            choices={options(picklists, 'priority')} value={priorities[s.key]}
            onChange={(v) => {
              const next: PriorityMap = Object.fromEntries(
                Object.entries(priorities).filter(([key]) => key !== s.key),
              );
              if (v != null) next[s.key] = v;
              set({ priority_map: next });
            }} />
        ))}
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-sm font-medium mb-1">Status changes</legend>
        <div className="space-y-1">
          <span className="text-sm font-medium">Statuses that mean someone has it</span>
          <p className="text-xs text-muted-foreground">
            Acknowledging the incident in Witina moves the ticket to the first one ticked.
          </p>
          <div className="grid grid-cols-2 gap-2 pt-1">
            {statuses.filter((o) => o.active).map((o) => {
              const id = Number(o.value);
              const checked = ackIds.includes(id);
              return (
                <label key={o.value} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={checked}
                    onCheckedChange={(on) => {
                      set({ ack_status_ids: on === true ? [...ackIds, id] : ackIds.filter((x) => x !== id) });
                    }} />
                  {o.label}
                </label>
              );
            })}
          </div>
        </div>
        <PicklistSelect id={field('recovery')} label="When the network recovers" choices={statuses}
          value={draft.recovery_status_id} onChange={(v) => { set({ recovery_status_id: v }); }}
          noneLabel="Add a note, leave the status alone"
          help="Witina says when the network is healthy; the ticket says when the work is done." />
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-sm font-medium mb-1">Notes</legend>
        <PicklistSelect id={field('note-type')} label="Note type" choices={options(picklists, 'noteType')}
          value={draft.note_type_id} onChange={(v) => { set({ note_type_id: v }); }} />
        <PicklistSelect id={field('publish')} label="Who can see notes" choices={options(picklists, 'publish')}
          value={draft.note_publish} onChange={(v) => { set({ note_publish: v }); }}
          help="Internal unless you choose otherwise: notes carry network detail and text devices report." />
        <div className="space-y-1">
          <Label htmlFor={field('udf')}>Field for the Witina link (optional)</Label>
          <Input id={field('udf')} className="max-w-sm" value={draft.link_udf ?? ''} placeholder="A ticket user-defined field"
            onChange={(e) => { set({ link_udf: e.target.value.trim() === '' ? null : e.target.value }); }} />
        </div>
      </fieldset>

      {update.isError ? (
        <p className="text-sm text-destructive">{apiErrorMessage(update.error, 'Couldn’t save the settings.')}</p>
      ) : null}
      {update.isSuccess ? <p className="text-sm text-muted-foreground">Saved.</p> : null}
      <Button type="submit" size="sm" disabled={update.isPending || missing}>
        {update.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
        Save settings
      </Button>
      {missing ? (
        <p className="text-xs text-muted-foreground">
          A status, a priority for each severity, a note type and who can see notes are needed before tickets can be made.
        </p>
      ) : null}
    </form>
  );
}
